export type TimekeeperScheduleAction =
    | 'add-shift'
    | 'edit-shift'
    | 'delete-shift';

export interface TimekeeperScheduleRoute {
    action: TimekeeperScheduleAction | null;
    shiftId: number | null;
    from: string | null;
    to: string | null;
    day: string | null;
    employeeIds: number[];
    tagIds: number[];
}

const BASE_PATH = '/timekeeper/scheduler';

const isDay = (value: string | null): boolean => {
    if (!value) {
        return false;
    }

    return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

const toId = (value: string | undefined): number | null => {
    if (!value || !/^\d+$/.test(value)) {
        return null;
    }

    return Number(value);
};

export const buildTimekeeperScheduleBasePath = (): string => BASE_PATH;

export const buildTimekeeperSchedulePath = (state: {
    action?: TimekeeperScheduleAction | null;
    shiftId?: number | string | null;
}): string => {
    const shiftId = state.shiftId;

    if (state.action === 'add-shift') {
        return `${BASE_PATH}/add-shift`;
    }

    if (state.action === 'edit-shift') {
        if (shiftId === undefined || shiftId === null || shiftId === '') {
            return BASE_PATH;
        }

        return `${BASE_PATH}/edit-shift/${shiftId}`;
    }

    if (state.action === 'delete-shift') {
        if (shiftId === undefined || shiftId === null || shiftId === '') {
            return BASE_PATH;
        }

        return `${BASE_PATH}/delete-shift/${shiftId}`;
    }

    return BASE_PATH;
};

export const buildTimekeeperScheduleSearch = (state: {
    from?: string | null;
    to?: string | null;
    day?: string | null;
    employeeIds?: number[];
    tagIds?: number[];
}): string => {
    const params = new URLSearchParams();

    if (state.from && state.to && isDay(state.from) && isDay(state.to)) {
        params.set('from', state.from);
        params.set('to', state.to);
    }

    if (state.day && isDay(state.day)) {
        params.set('day', state.day);
    }

    if (state.employeeIds && state.employeeIds.length > 0) {
        params.set('employees', state.employeeIds.join(','));
    }

    if (state.tagIds && state.tagIds.length > 0) {
        params.set('tags', state.tagIds.join(','));
    }

    const query = params.toString();
    return query ? `?${query}` : '';
};

export const parseTimekeeperScheduleRoute = (
    pathname: string,
    search: string
): TimekeeperScheduleRoute => {
    const segments = pathname.split('/').filter(Boolean);

    if (segments[0] !== 'timekeeper' || segments[1] !== 'scheduler') {
        return {
            action: null,
            shiftId: null,
            from: null,
            to: null,
            day: null,
            employeeIds: [],
            tagIds: [],
        };
    }

    const maybeAction = segments[2] as TimekeeperScheduleAction | undefined;
    let action: TimekeeperScheduleAction | null = null;
    let shiftId: number | null = null;

    if (maybeAction === 'add-shift') {
        action = 'add-shift';
    } else if (maybeAction === 'edit-shift') {
        action = 'edit-shift';
        shiftId = toId(segments[3]);
    } else if (maybeAction === 'delete-shift') {
        action = 'delete-shift';
        shiftId = toId(segments[3]);
    }

    const params = new URLSearchParams(search);
    const from = params.get('from');
    const to = params.get('to');
    const day = params.get('day');
    const employeesRaw = params.get('employees');
    const tagsRaw = params.get('tags');

    const employeeIds = (employeesRaw || '')
        .split(',')
        .map((value) => value.trim())
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value));

    const tagIds = (tagsRaw || '')
        .split(',')
        .map((value) => value.trim())
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value));

    return {
        action,
        shiftId,
        from: isDay(from) ? from : null,
        to: isDay(to) ? to : null,
        day: isDay(day) ? day : null,
        employeeIds,
        tagIds,
    };
};